import "./WmgPublicDashboard.scss";
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { ClassDisplayOptions, StatusDisplayOptions, WatershedReportPublicFacingConfig, WatershedReportPublicFacingConfigLinkInfo } from "../../../../models/viewModels/WatershedReportPublicFacingConfigViewModel";
import { PublicElementSection } from "../previewPublic/PublicElementSection";
import { SnapshotToBmpGeneral, WatershedReportSnapshotBmp } from "../../../../models/WatershedReportSnapshot";
import { DashboardBubbles } from "./dashboardBubbles/DashboardBubbles";
import WelcomeViewModel from "../../../../models/viewModels/WelcomeViewModel";
import { PublicConfigLinks } from "../PublicConfigLinks";
import { ProgramSummaryTable } from "./ProgramSummaryTable";
import { ProgramProgressGraph } from "./ProgramProgressGraph";
import { useDimensions } from "../../../../core/UseDimensions";
import { ProjectDetailsMap } from "./Map/ProjectDetailsMap";
import { ProgramSummaryVisual } from "./ProgramSummaryVisual";
import { ProjectList } from "../../manageProjectData/ProjectList";
import BmpGeneral from "../../../../models/BmpGeneral";
import { BmpClassId } from "../../../../models/lutModels/LutBmpClass";
import { PrettyProjectPageTypes } from "../../../mainWrapper/MainWrapper";
import { SidebarContext, SidebarTypes } from "../../../../core/SidebarComponent";

export interface IWMGPublicDashboard {
    wmgId: number;
    wmgPublicData: Array<WatershedReportSnapshotBmp>; // came from GenerateData
    wmgPublicInfo: WatershedReportPublicFacingConfig;
    wmgPublicInfoLinks: Array<WatershedReportPublicFacingConfigLinkInfo>;
    publicOrInternal: "public" | "internal";
    useLiveData?: boolean;
    setSidebarVisible?: Dispatch<SetStateAction<boolean>>;
    snapshotDate?: Date;
}

export const WMGPublicDashboard = (props: IWMGPublicDashboard) => {
    const [bubbleInfo, setBubbleInfo] = useState(new WelcomeViewModel());

    const [divRef, dimensions] = useDimensions();
    const chartWidth = useMemo(() => {
        if (!dimensions) return 0;

        return dimensions.width > 1000 ? 1000 : dimensions.width * 0.9
    }, [dimensions])

    const {
        setSidebarProjectInfoObj,
        setSidebarVisible,
        dispatchSidebar
    } = useContext(SidebarContext);

    const handleDetailsClick = (e: any, wmgPublicData) => {
        if (!e.results[0] || e.results[0].layer.id === "New & Redevelopment Projects") {
            return;
        }
        const bmpSnap = wmgPublicData.find(project => project.bmpGeneralId === e.results[0]?.graphic.attributes.bmpGeneralId)
        const bmpGeneralData = SnapshotToBmpGeneral(bmpSnap, props.wmgId);
        setSidebarProjectInfoObj(bmpGeneralData);
        dispatchSidebar({
            type: "open",
            payload: { name: "PrettyProjectPage", content: { prettyProjectPageType: PrettyProjectPageTypes.BmpGeneral } },
        });
    }

    useEffect(() => {
        let newBubbleData: WelcomeViewModel = new WelcomeViewModel();
        let filteredBMPArray = props.wmgPublicData.filter((bmp) => (bmp.lutBmpClassId === BmpClassId.WatershedControlMeasure || bmp.lutBmpClassId === BmpClassId.NonStructural) && bmp.lutBmpStatusId === 1024);
        newBubbleData.numberOfProjects = filteredBMPArray.length;
        newBubbleData.numberParkBenefit = filteredBMPArray.reduce((sum, bmp) => { return sum + bmp.coBenefitParks }, 0);
        newBubbleData.numberHeatIslandBenefit = filteredBMPArray.reduce((sum, bmp) => { return sum + bmp.coBenefitHeatIsland }, 0);
        newBubbleData.numberWaterSupplyBenefit = filteredBMPArray.reduce((sum, bmp) => { return sum + bmp.coBenefitWaterSupplyAugmentation }, 0);
        newBubbleData.numberBeautificationBenefit = filteredBMPArray.reduce((sum, bmp) => { return sum + bmp.coBenefitNeighborhood }, 0);
        newBubbleData.numberDacBenefit = filteredBMPArray.reduce((sum, bmp) => { return sum + bmp.coBenefitDisadvantaged }, 0);
        setBubbleInfo(newBubbleData);
    }, [props.wmgPublicData]);

    // this is for Project Details Table
    const onProjectListInfoClick = async (selectedBmpId: number, viewType: SidebarTypes) => {
        setSidebarProjectInfoObj(SnapshotToBmpGeneral(props.wmgPublicData.find(x => x.bmpGeneralId === selectedBmpId)!, props.wmgId));
        if (props.publicOrInternal === "internal") {
            dispatchSidebar({
                type: "open",
                payload: { name: viewType, content: { showEditButton: false, show24HourCapacity: true, prettyProjectPageType: PrettyProjectPageTypes.BmpGeneral } },
            });
        } else {
            setSidebarVisible(true);
        }
    };

    const projectListBmps: BmpGeneral[] = useMemo(() => {
        // Dont bother with this ETL if not enabled
        if (!props.wmgPublicInfo.projectDetailsTableEnabled) {
            return [];
        }
        var bmps = props.wmgPublicData
            .filter(x => (props.wmgPublicInfo.projectDetailsTableClass === ClassDisplayOptions.All && x.lutBmpClassId === BmpClassId.NonStructural) || x.lutBmpClassId === BmpClassId.WatershedControlMeasure)
            .map(x => SnapshotToBmpGeneral(x, props.wmgId));

        if (props.wmgPublicInfo.projectDetailsTableStatus === StatusDisplayOptions.CompleteOnly) {
            bmps = bmps.filter(x => x.bmpStatusId === 1024);
        }

        return bmps;
    }, [props.wmgId, props.wmgPublicData, props.wmgPublicInfo.projectDetailsTableClass, props.wmgPublicInfo.projectDetailsTableEnabled, props.wmgPublicInfo.projectDetailsTableStatus])

    return (
        <div className="wmg-public-dashboard">
            {/* PROGRAM MESSAGE SECTION */}
            <PublicElementSection
                sectionTitle="Program Description"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty="programMessageDescription"
                visible={props.wmgPublicInfo.programMessageEnabled}
            />

            {/* PROGRAM SUMMARY VISUAL SECTION */}
            <PublicElementSection
                sectionTitle="Program Summary Visual"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"programSummaryVisualizationDescription"}
                visible={props.wmgPublicInfo.programSummaryVisualizationEnabled}>
                <ProgramSummaryVisual
                    wmgId={props.wmgId}
                    bmpData={props.wmgPublicData}
                    publicConfig={props.wmgPublicInfo}
                    graphHeight={"35rem"}
                />
            </PublicElementSection>

            {/* PROGRAM MULTIPLE BENEFITS SUMMARY SECTION */}
            <PublicElementSection
                sectionTitle="Program Community Co-Benefits Summary"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"programSummaryMultiBenefitsDescription"}
                visible={props.wmgPublicInfo.programSummaryMultiBenefitsEnabled}
            >
                {props.wmgPublicData.length > 0 && (props.wmgPublicInfo.showCoBenefitHeatIsland || props.wmgPublicInfo.showCoBenefitNeighborhood || props.wmgPublicInfo.showCoBenefitWaterSupplyAugmentation
                    || props.wmgPublicInfo.showCoBenefitDisadvantaged || props.wmgPublicInfo.showCoBenefitParks || props.wmgPublicInfo.showCoBenefitCleanStreets
                    || props.wmgPublicInfo.showCoBenefitFlooding || props.wmgPublicInfo.showCoBenefitJobCreation)
                    ?
                    // Show if there are BMPs and at least one selected co-benefit
                    <div className="program-multibenefits-summary">
                        <DashboardBubbles
                            bubbleInfo={bubbleInfo}
                            showReducesHeatIslandEffect={props.wmgPublicInfo.showCoBenefitHeatIsland}
                            showNeighborhoodBeautification={props.wmgPublicInfo.showCoBenefitNeighborhood}
                            showWaterSupplyAugmentation={props.wmgPublicInfo.showCoBenefitWaterSupplyAugmentation}
                            showBenefitsToDacs={props.wmgPublicInfo.showCoBenefitDisadvantaged}
                            showProvidesParksAndGreenSpaces={props.wmgPublicInfo.showCoBenefitParks}
                            showCleanStreets={props.wmgPublicInfo.showCoBenefitCleanStreets}
                            showReducesFlooding={props.wmgPublicInfo.showCoBenefitFlooding}
                            showJobCreation={props.wmgPublicInfo.showCoBenefitJobCreation}
                        />
                    </div>
                    :
                    <div className="graph-no-data-wrapper">
                        <div className="graph-no-data">
                            No Co-Benefit Data
                        </div>
                    </div>
                }
            </PublicElementSection>

            {/* PROGRAM PROGRESS SECTION */}
            <div ref={divRef}> {/* for getting the width of the charts */}
                <PublicElementSection
                    sectionTitle="Program Progress"
                    wmgPublicInfo={props.wmgPublicInfo}
                    sectionDescriptionProperty={"programProgressDescription"}
                    visible={props.wmgPublicInfo.programProgressEnabled}
                >
                    <ProgramProgressGraph
                        wmgId={props.wmgId}
                        bmpData={props.wmgPublicData}
                        publicConfig={props.wmgPublicInfo}
                        graphHeight={500}
                        snapshotDate={props.snapshotDate}
                    />
                </PublicElementSection>
            </div>
            {/* PROGRAM SUMMARY TABLE SECTION */}
            <PublicElementSection
                sectionTitle="Program Summary Table"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"programSummaryTableDescription"}
                visible={props.wmgPublicInfo.programSummaryTableEnabled}
            >
                <ProgramSummaryTable
                    publicConfig={props.wmgPublicInfo}
                    wmgPublicData={props.wmgPublicData}
                />
            </PublicElementSection>

            {/* PROJECT DETAILS MAP SECTION */}
            <PublicElementSection
                sectionTitle="Project Details Map"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"projectDetailsMapDescription"}
                visible={props.wmgPublicInfo.projectDetailsMapEnabled}
            >
                <ProjectDetailsMap
                    wmgId={props.wmgId}
                    wmgPublicInfo={props.wmgPublicInfo}
                    wmgPublicData={props.wmgPublicData}
                    width={chartWidth}
                    handleDetailsClick={handleDetailsClick}
                    useLiveData={props.useLiveData}
                />
            </PublicElementSection>

            {/* PROJECT DETAILS TABLE SECTION */}
            <PublicElementSection
                sectionTitle="Project Details Table"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"projectDetailsTableDescription"}
                visible={props.wmgPublicInfo.projectDetailsTableEnabled}
            >
                <div className="project-details-table">
                    <ProjectList
                        bmpGeneral={projectListBmps}
                        onInfoClick={onProjectListInfoClick}
                        maxPerPage={10}
                        prettyProjectButtonTitle="Details"
                        hideNonStructuralInfo
                        publicOrInternal={props.publicOrInternal}
                    />
                </div>
            </PublicElementSection>

            {/* ADDITIONAL INFO SECTION */}
            <PublicElementSection
                sectionTitle="Additional Information"
                wmgPublicInfo={props.wmgPublicInfo}
                sectionDescriptionProperty={"additionalInfoDescription"}
                visible={props.wmgPublicInfo.additionalInfoEnabled}
            >
                <PublicConfigLinks
                    wmgId={props.wmgId}
                    wmgPublicInfoLinks={props.wmgPublicInfoLinks}
                    editable={false}
                />
            </PublicElementSection>
        </div>
    )
}
